import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ItemGroup } from '@/types/itemGroup';

// Mock data - replace with API calls
const mockItemGroups: ItemGroup[] = [
  {
    id: '1',
    code: 'MED',
    name: 'Medicines',
    itemType: 'Inventory',
    isActive: true,
    createdAt: '2023-01-15T10:30:00Z',
    updatedAt: '2023-01-15T10:30:00Z',
  },
  {
    id: '2',
    code: 'LAB',
    name: 'Lab Tests',
    itemType: 'Service',
    isActive: true,
    createdAt: '2023-02-20T14:45:00Z',
    updatedAt: '2023-02-20T14:45:00Z',
  },
  {
    id: '3',
    code: 'EQP',
    name: 'Equipment',
    itemType: 'Asset',
    isActive: false,
    createdAt: '2023-03-10T09:15:00Z',
    updatedAt: '2023-03-10T09:15:00Z',
  },
];

const ItemGroupList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemGroups, setItemGroups] = useState<ItemGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Load item groups (replace with API call)
  useEffect(() => {
    const fetchItemGroups = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        setItemGroups(mockItemGroups);
      } catch (error) {
        console.error('Error fetching item groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchItemGroups();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (_event: React.ChangeEvent<HTMLInputElement>) => {
    // The event is not used, but we need to keep it for the function signature
    const value = _event.target.value;
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleAddNew = () => {
    navigate('/settings/item-groups/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/settings/item-groups/${id}/edit`);
  };

  const handleView = (id: string) => {
    navigate(`/settings/item-groups/${id}`);
  };

  const handleDelete = (id: string) => {
    // Implement delete functionality
    if (window.confirm('Are you sure you want to delete this item group?')) {
      console.log('Delete item group:', id);
      // Call API to delete
    }
  };

  // Filter item groups based on search term
  const filteredItemGroups = itemGroups.filter(itemGroup =>
    itemGroup.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    itemGroup.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    itemGroup.itemType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }


  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Item Groups
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Search item groups..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Item Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItemGroups.length > 0 ? (
                filteredItemGroups
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((itemGroup) => (
                    <TableRow hover key={itemGroup.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {itemGroup.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {itemGroup.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={itemGroup.itemType} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={itemGroup.isActive ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                          label={itemGroup.isActive ? 'Active' : 'Inactive'}
                          color={itemGroup.isActive ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton 
                              onClick={() => handleView(itemGroup.id)}
                              size="small"
                              color="info"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton 
                              onClick={() => handleEdit(itemGroup.id)}
                              size="small"
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              onClick={() => handleDelete(itemGroup.id)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No item groups found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredItemGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default ItemGroupList;
